import React, { memo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';

import { CallStatus } from 'constants/telephony';
import { getIsMobileLayoutForced, getIsNativePlatform } from 'utils/platform';

import { selectCallState, selectCallStatus } from 'store/system/selectors';

import InCallControls from './components/InCallControls';
import IncomingCallActions from './components/IncomingCallActions';
import PostCallSheet from './components/PostCallSheet';
import { ONE_SECOND_MS } from './MobileCallSurface.constants';
import {
  callerNameStyles,
  callerNumberStyles,
  identityStyles,
  overlayStyles,
  spacerStyles,
  stateLabelStyles,
  timerStyles,
} from './MobileCallSurface.styles';
import { MobileCallSurfaceTestIds } from './MobileCallSurface.testIds';
import {
  formatDuration,
  getCallerDisplayName,
  getCallerDisplayNumber,
  getCallStateLabel,
} from './MobileCallSurface.utils';

const MobileCallSurface = () => {
  const callStatus = useSelector(selectCallStatus);
  const callState = useSelector(selectCallState);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const isNative = getIsNativePlatform();
  const isMobileNav = isNative || getIsMobileLayoutForced();
  const isActive = callStatus === CallStatus.ACTIVE;
  const isIncoming = callStatus === CallStatus.INCOMING;
  // DEV-4332: on native, CallKit is the SOLE incoming/ringing answer surface. Suppress the in-app
  // incoming presentation (no second answer/decline UI) so the two surfaces don't race the SIP answer.
  // The in-app surface only takes over once the call is active (in-call controls), after a CallKit answer.
  const isNativeIncomingSuppressed = isNative && isIncoming;
  const { startTime } = callState;

  useEffect(() => {
    if (!isActive || !startTime) {
      setElapsedSeconds(0);

      return;
    }

    setElapsedSeconds(Math.floor((Date.now() - startTime) / ONE_SECOND_MS));

    const interval = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startTime) / ONE_SECOND_MS));
    }, ONE_SECOND_MS);

    return () => clearInterval(interval);
  }, [isActive, startTime]);

  if (!isMobileNav || callStatus === CallStatus.IDLE || isNativeIncomingSuppressed) {
    return null;
  }

  if (callStatus === CallStatus.POST_CALL) {
    return <PostCallSheet isOpen />;
  }

  const displayName = getCallerDisplayName(callState);
  const displayNumber = getCallerDisplayNumber(callState);
  const stateLabel = getCallStateLabel({
    callStatus,
    telnyxCallState: callState.telnyxCallState,
    isOnHold: callState.isOnHold,
  });

  return (
    <Box data-testid={MobileCallSurfaceTestIds.ROOT} sx={overlayStyles}>
      <Box sx={identityStyles}>
        <Typography sx={callerNameStyles} variant="bold32">
          {displayName}
        </Typography>
        {displayNumber && displayNumber !== displayName && (
          <Typography sx={callerNumberStyles} variant="regular16">
            {displayNumber}
          </Typography>
        )}
        <Typography sx={stateLabelStyles} variant="regular16">
          {stateLabel}
        </Typography>
        {isActive && (
          <Typography sx={timerStyles} variant="regular14">
            {formatDuration(elapsedSeconds)}
          </Typography>
        )}
      </Box>

      <Box sx={spacerStyles} />

      {isIncoming ? <IncomingCallActions /> : <InCallControls />}
    </Box>
  );
};

export default memo(MobileCallSurface);
